import { STATUS_CODES } from 'utils/Constants'
import config from 'components/GetStarted/config'
import { getStepIndexFromUrlSlug } from 'utils/getStartedUtils'
import { DEFAULT_STEP } from 'components/GetStarted/constants'
import { siteDomain } from 'utils/CareguideDomains'

/**
 * This mimics how a middleware stack would work in express.
 * You create middleware function by passing an array of redirect handlers.
 * Each redirect handler should have the following interface:
 *
 * const handler = (nextContext, next) => {
 *    // check if should redirect
 *    // if no make sure to call next() to move onto next redirect handler
 * }
 *
 * If one of the redirect handlers succeeds the remaining handlers won't be run.
 * If all redirect handlers run and result in no redirect the page will render as per usual.
 *
 * @param {Array} redirectHandlers
 * @returns {Boolean} returns true if one of the redirects succeeds, and false if not
 */
export const getRedirectMiddleware = (redirectHandlers = []) => async (nextContext) => {
  const isServerSide = !!(nextContext?.req && nextContext?.res && nextContext?.req?.url)

  if (!isServerSide) {
    return false
  }

  let curRedirectIndex = 0
  const fistRedirect = redirectHandlers[0]

  const next = async () => {
    curRedirectIndex++
    const nextReidrect = redirectHandlers[curRedirectIndex]
    if (nextReidrect) {
      await nextReidrect(nextContext, next)
    }
  }

  if (fistRedirect) {
    await fistRedirect(nextContext, next)
  }

  return curRedirectIndex < redirectHandlers.length
}

const getAutologinRedirectUrl = (req, autologinToken) => {
  const tokenEncoded = encodeURIComponent(autologinToken)
  const tokenPattern = new RegExp(`altkn=(${autologinToken}|${tokenEncoded})`, 'g')
  const autologinRedirectUrl = req.url.replace(tokenPattern, '')

  if (autologinRedirectUrl.slice(-1) === '?') {
    return autologinRedirectUrl.slice(0, -1)
  }
  return autologinRedirectUrl
}

export const handleAutoLoginRedirect = async (ctx, next) => {
  const autologinToken = ctx?.query?.altkn
  const { req, res } = ctx

  if (!autologinToken) {
    return next()
  }

  const gqlEndpoint = process.env.CGC_WEB_CGC_APP_URL || 'http://cgc-app:3000'
  const url = `${gqlEndpoint}/autologin?autologin_token=${autologinToken}`
  const btoa = require('btoa')
  // eslint-disable-next-line no-undef
  const response = await fetch(url, {
    headers: {
      Authorization: `Basic ${btoa('careguidebeta:testdemo')}`,
      'x-cgc-domain': siteDomain(ctx),
    },
  })
  const responseCookies = response.headers.get('set-cookie')
  const jsonResponse = await response.json()
  const autologinRedirectUrl = getAutologinRedirectUrl(req, autologinToken)
  let headers = { Location: autologinRedirectUrl }

  if (jsonResponse.success === true) {
    const setCookie = require('set-cookie-parser')
    const splitCookieHeaders = setCookie.splitCookiesString(responseCookies)
    headers = { ...headers, 'Set-Cookie': splitCookieHeaders }
  }

  res.writeHead(STATUS_CODES.CODE302, headers)
  res.end()
}

export const handleNearRedirect = (ctx, next) => {
  const { req, res } = ctx
  if (req.url.includes('/near')) {
    res.writeHead(STATUS_CODES.CODE301, { Location: req.url.replace('/near', '') })
    res.end()
  } else {
    next()
  }
}

export const handleInvalidGetStartedStepRedirect = (ctx, next) => {
  const { req, res } = ctx
  const isGetStartedRoute = req?.url?.includes('/get-started')
  if (!isGetStartedRoute) {
    return next()
  }

  const currentPageIndex = getStepIndexFromUrlSlug(config.steps, ctx?.query?.step)
  const isValidPage = currentPageIndex !== null && currentPageIndex !== -1
  const originalUrl = req?.url ?? ''
  const queryStartIndex = originalUrl.indexOf('?')
  const originalQuery = queryStartIndex >= 0 ? originalUrl.substring(queryStartIndex) : ''

  // if invalid page server side redirect to default step
  if (!isValidPage) {
    res.writeHead(STATUS_CODES.CODE301, {
      Location: `/get-started/${DEFAULT_STEP}${originalQuery}`,
    })
    res.end()
  } else {
    next()
  }
}

export const handle404Redirect = (ctx, next) => {
  const { req, res } = ctx
  const errorCode = res && res.statusCode > 200 ? res.statusCode : null

  if (errorCode === STATUS_CODES.CODE404) {
    /**
     * Replaces slashes
     * Test vectors:
     * `/test/test/` -> `/test/test`
     * `/test/////test////` -> `/test/test`
     * `/test//test//?a=1&b=2` -> `/test?a=1&b=2`
     * `/test///#test` -> `/test#test`
     */
    const correctPath = (invalidPath) =>
      invalidPath
        .replace(/\/+$/, '')
        .replace(/\/+#/, '#')
        .replace(/\/+\?/, '?')
        .replace(/\/+/g, '/')
    // Do a 301 Permenant redirect to correct path.
    if (correctPath(req.url) !== req.url) {
      res.writeHead(STATUS_CODES.CODE301, { Location: correctPath(req.url) })
      res.end()
    }
  } else {
    next()
  }
}
